import React, { useEffect, useState } from 'react';
import { Clock, Info } from '@phosphor-icons/react';
import TaskDistributionSectionHeading from './TaskDistributionSectionHeading.jsx';
import {
  getOptimizationCriterion,
  OPTIMIZATION_CRITERIA
} from '../utils/taskDistributionUtils.jsx';

const formatShiftStartTime = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
};

const isValidShiftStartTime = (value) => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return false;

  return Number(match[1]) <= 23 && Number(match[2]) <= 59;
};

const TaskDistributionJourneyStep = ({
  date,
  dateOptions,
  config,
  selectedPriorityPreset,
  onDateChange,
  onConfigChange
}) => {
  const [shiftStartTimeInput, setShiftStartTimeInput] = useState(config.shiftStartTime || '');
  const selectedOptimizationCriterion = getOptimizationCriterion(config.optimizationCriterion);
  const selectedCriterionIndex = OPTIMIZATION_CRITERIA.findIndex(
    (criterion) => criterion.value === selectedOptimizationCriterion.value
  );
  const criterionSliderStep = 100 / Math.max(OPTIMIZATION_CRITERIA.length - 1, 1);
  const [criterionSliderValue, setCriterionSliderValue] = useState(
    () => selectedCriterionIndex * criterionSliderStep
  );
  const sliderCriterionIndex = Math.max(
    0,
    Math.min(
      OPTIMIZATION_CRITERIA.length - 1,
      Math.round(criterionSliderValue / criterionSliderStep)
    )
  );
  const sliderCriterion = OPTIMIZATION_CRITERIA[sliderCriterionIndex];

  useEffect(() => {
    setShiftStartTimeInput(config.shiftStartTime || '');
  }, [config.shiftStartTime]);

  useEffect(() => {
    setCriterionSliderValue(selectedCriterionIndex * criterionSliderStep);
  }, [criterionSliderStep, selectedCriterionIndex]);

  const handleShiftStartTimeChange = (event) => {
    const nextValue = formatShiftStartTime(event.target.value);
    setShiftStartTimeInput(nextValue);

    if (!nextValue || isValidShiftStartTime(nextValue)) {
      onConfigChange('shiftStartTime', nextValue);
    }
  };

  const handleShiftStartTimeBlur = () => {
    if (shiftStartTimeInput && !isValidShiftStartTime(shiftStartTimeInput)) {
      setShiftStartTimeInput(config.shiftStartTime || '');
    }
  };

  const commitOptimizationCriterion = () => {
    const nextCriterionIndex = Math.max(
      0,
      Math.min(
        OPTIMIZATION_CRITERIA.length - 1,
        Math.round(criterionSliderValue / criterionSliderStep)
      )
    );
    const nextCriterion = OPTIMIZATION_CRITERIA[nextCriterionIndex];
    setCriterionSliderValue(nextCriterionIndex * criterionSliderStep);

    if (nextCriterion.value !== selectedOptimizationCriterion.value) {
      onConfigChange('optimizationCriterion', nextCriterion.value);
    }
  };

  return (
    <div className="space-y-5">
    <TaskDistributionSectionHeading
      title="Fecha y jornada"
      description="Elige cuándo comenzarán los recorridos."
    />

    <div data-tour="dist-date">
      <span className="mb-2 block text-sm font-semibold text-content">Fecha</span>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {dateOptions.map((option) => {
          const isSelected = option.value === date;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onDateChange(option.value)}
              className={`min-w-0 rounded-2xl border px-1.5 py-3 text-center transition ${
                isSelected
                  ? 'border-amber bg-amber-hover/10'
                  : 'border-line bg-surface-2 hover:border-line'
              }`}
            >
              <span className={`block truncate text-[9px] font-semibold uppercase tracking-[0.12em] ${isSelected ? 'text-accent-text/80' : 'text-content-muted'}`}>
                {option.weekdayLabel}
              </span>
              <span className="mt-1 block text-xl font-semibold text-content">{option.dayLabel}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs leading-5 text-content-muted">
        Puedes planificar recorridos desde hoy hasta 4 días hacia adelante.
      </p>
    </div>

    <label data-tour="dist-hour" className="block">
      <span className="mb-2 block text-sm font-semibold text-content">Hora inicio jornada</span>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          maxLength="5"
          value={shiftStartTimeInput}
          onChange={handleShiftStartTimeChange}
          onBlur={handleShiftStartTimeBlur}
          placeholder="hh : mm"
          aria-label="Hora inicio jornada, formato hora y minutos"
          className="w-full rounded-xl border border-line bg-surface-2 px-3 py-3 pr-11 text-base font-medium text-content placeholder:text-content-muted outline-none transition focus:border-amber"
        />
        <Clock size={18} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-content-muted" />
      </div>
    </label>

    <div data-tour="dist-criterion">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-content">Criterio de optimización</span>
        <span className="text-xs font-semibold text-accent-text">{sliderCriterion.label}</span>
      </div>
      <div className="relative flex h-9 items-center">
        <div className="pointer-events-none absolute inset-x-0 h-2 rounded-full bg-surface-2" />
        <div className="pointer-events-none absolute inset-x-0 flex justify-between">
          {OPTIMIZATION_CRITERIA.map((criterion, index) => (
            <span
              key={criterion.value}
              className={`h-2.5 w-2.5 rounded-full border-2 border-line ${
                index <= sliderCriterionIndex ? 'bg-amber-hover' : 'bg-content-muted'
              }`}
            />
          ))}
        </div>
        <span
          className="pointer-events-none absolute top-1/2 z-10 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-amber-hover shadow-[0_2px_10px_rgb(var(--amber-rgb)/0.55)] transition-[left] duration-75"
          style={{ left: `${criterionSliderValue}%` }}
        />
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={criterionSliderValue}
          onChange={(event) => setCriterionSliderValue(Number(event.target.value))}
          onPointerUp={commitOptimizationCriterion}
          onKeyUp={commitOptimizationCriterion}
          aria-label="Criterio de optimización"
          aria-valuetext={sliderCriterion.label}
          aria-describedby="optimization-criterion-description"
          className="optimization-criterion-slider relative z-20 h-9 w-full cursor-grab appearance-none bg-transparent focus:cursor-grabbing focus:outline-none"
        />
      </div>
      <div className="mt-2 grid grid-cols-3 text-[10px] font-medium text-content-muted">
        {OPTIMIZATION_CRITERIA.map((criterion, index) => (
          <span
            key={criterion.value}
            className={index === 0 ? 'text-left' : index === OPTIMIZATION_CRITERIA.length - 1 ? 'text-right' : 'text-center'}
          >
            {criterion.label}
          </span>
        ))}
      </div>
      <p id="optimization-criterion-description" className="mt-3 rounded-xl border border-line bg-surface-2 px-3 py-2 text-xs leading-5 text-content-muted">
        {sliderCriterion.description}
      </p>
    </div>

    <div className="rounded-2xl border border-line bg-surface-2 px-4 py-4 text-sm text-content">
      <div className="flex items-center gap-2 font-semibold">
        <Info size={17} weight="bold" className="text-content-muted" />
        Estrategia zonal
      </div>
      <p className="mt-2 text-xs leading-5 text-content-muted">
        Usa tareas con equipo definido como punto inicial de zona. Si no hay, reparte sectores desde la base,
        asigna cada tarea al equipo más cercano validando factibilidad real y construye el recorrido dentro de cada zona.
      </p>
    </div>
    </div>
  );
};

export default TaskDistributionJourneyStep;
